/**
 * The edit model's table operations, checked against the reader's paragraph
 * list and turned into the row changes a writer places.
 *
 * Three operations act on whole rows or a whole table, beside the paragraph
 * operations in `edit-operations.ts`:
 *
 * - `insert_table_row_after` names any paragraph of a template row and gives
 *   one or more new rows, each one text per cell of that row, and says what a
 *   new row does under a vertically merged cell.
 * - `delete_table_row` names any paragraph of the row to strike.
 * - `insert_table_after` names the paragraph a new table follows, gives its
 *   rows, the first of which is the header row, and says where it goes: right
 *   after that paragraph (inside the cell, when the paragraph is a cell, so
 *   tables nest) or after the table holding it.
 *
 * Every check the writer would otherwise fail on is made here, from the
 * paragraph list alone, so a re-ask reaches the model as a 422 it can act on
 * and the writer never sees an operation it cannot place. A paragraph is
 * looked up by `bookmarkNames.includes(paragraph_id)`, the rule every other
 * lookup in this package uses.
 */
import { z } from "zod";

import { LEFT_OUT, type OperationReaskPolicy, WRITTEN_AS_GIVEN } from "./operation-reask-policy";
import type { DocumentTableCell, TableCellPath } from "./wire/table-cell-path";
import { tablePlacementSchema } from "./wire/table-placement";

/** What a new row does under a vertically merged cell of its template row; the model chooses per operation. */
export const mergedCellsBehaviourSchema = z.enum(["join", "start_new"]);

const rowTextsSchema = z.array(z.string());
/**
 * The rows of a table being inserted, in the one shape both sides of the
 * operation agree on.
 *
 * WHY
 *   `insert_table_after` and `desiredTableInsertion` are the same insertion before
 *   and after its bookmark is resolved — `TableOperations.tableInsertion` turns one
 *   into the other — so a table with no rows, or a row with no cells, is as
 *   meaningless on the wire as it is in the operation. Written once, they cannot
 *   drift: `rows: []` reached the writer and threw on `rows[0].length`, and
 *   `rows: [[]]` reached it silently and asked Word for a table with no columns.
 */
const tableInsertionRowsSchema = z.array(rowTextsSchema.min(1)).min(1);

export const tableEditOperationSchema = z.discriminatedUnion("op", [
    z.object({ op: z.literal("insert_table_row_after"), paragraph_id: z.string().min(1), rows: z.array(rowTextsSchema).min(1), merged_cells: mergedCellsBehaviourSchema }),
    z.object({ op: z.literal("delete_table_row"), paragraph_id: z.string().min(1) }),
    z.object({ op: z.literal("insert_table_after"), paragraph_id: z.string().min(1), rows: tableInsertionRowsSchema, place: tablePlacementSchema }),
]);

export type TableEditOperation = z.infer<typeof tableEditOperationSchema>;
export type MergedCellsBehaviour = z.infer<typeof mergedCellsBehaviourSchema>;

/** The `op` names of the three table operations; what tells a table operation from a paragraph one in a mixed list. */
export const TABLE_EDIT_OPERATION_NAMES: ReadonlySet<string> = new Set(tableEditOperationSchema.options.map((option) => option.shape.op.value));

/** Any operation the model may return, table or paragraph, as far as this module needs to know it: by its `op`. */
export interface NamedEditOperation {
    op: string;
}

/**
 * RETURNS
 *   true for the three table operations, false for every paragraph operation.
 *
 * WHY
 *   The route validates the whole mixed list against the operation union before
 *   anything is applied, so the shape behind a table `op` name is already the
 *   table operation's; the guard only has to read the name.
 */
export const isTableEditOperation = (operation: NamedEditOperation): operation is TableEditOperation => TABLE_EDIT_OPERATION_NAMES.has(operation.op);

/** What the operations need to know about one reader paragraph; `DocumentParagraph` satisfies it once it carries `tableCellPath`. */
export interface TableOperationParagraph {
    paragraphIndex: number;
    bookmarkNames: string[];
    tableCellPath: TableCellPath;
    /** True when Word numbers the paragraph itself, so a number typed into a cell cloned from it would render twice. */
    isAutoNumbered: boolean;
}

/**
 * A typed list marker at the start of a cell text, the shapes the core's
 * `TYPED_LIST_MARKER_RE` names ("3.", "3)", "1.2", "(c)", "iii.", "a)"), as
 * the whole text or followed by whitespace.
 *
 * EXAMPLE
 *   "3."          -> matches (the whole text)
 *   "3. Health"   -> matches
 *   "3.5%"        -> no match: the marker runs into "5%"
 *   "(c) data"    -> matches
 */
const TYPED_LIST_MARKER_AT_START = /^(?:\d+(?:\.[0-9a-zA-Z]+)*[.)]|\d+(?:\.\d+)+|[a-zA-Z][.)]|[ivxlcdmIVXLCDM]{1,4}[.)]|\([0-9a-zA-Z]{1,4}\))(?:\s|$)/;

/** One cell of the template row as the checks see it: its record and whether Word numbers any of its paragraphs. */
interface TemplateRowCell {
    cell: DocumentTableCell;
    isAutoNumbered: boolean;
}

/** New rows after the row that holds the named paragraph, in the order given. */
export const desiredTableRowInsertionSchema = z.object({
    afterRowOfBookmark: z.string(),
    rows: z.array(rowTextsSchema),
    mergedCells: mergedCellsBehaviourSchema,
});

/** The row that holds the named paragraph becomes a tracked deletion. */
export const desiredTableRowDeletionSchema = z.object({ rowOfBookmark: z.string() });

/** A new table placed by `place` relative to the named paragraph; `rows[0]` is its header row. */
export const desiredTableInsertionSchema = z.object({ afterBookmark: z.string(), rows: tableInsertionRowsSchema, place: tablePlacementSchema });

/** The table changes the apply route accepted, as a card and a section write carry them. */
export const tableOperationsApplyResultSchema = z.object({
    rowInsertions: z.array(desiredTableRowInsertionSchema),
    rowDeletions: z.array(desiredTableRowDeletionSchema),
    tableInsertions: z.array(desiredTableInsertionSchema),
});

export type DesiredTableRowInsertion = z.infer<typeof desiredTableRowInsertionSchema>;
export type DesiredTableRowDeletion = z.infer<typeof desiredTableRowDeletionSchema>;
export type DesiredTableInsertion = z.infer<typeof desiredTableInsertionSchema>;
export type TableOperationsApplyResult = z.infer<typeof tableOperationsApplyResultSchema>;

/** The result of a proposal that asked for no table change; what a remedy stored before table operations existed reads as. */
export const NO_TABLE_CHANGES: TableOperationsApplyResult = { rowInsertions: [], rowDeletions: [], tableInsertions: [] };

export interface TableOperationsApplyInput {
    /** The model's whole list, paragraph operations included; those are skipped, so every index reported is the position in this list. */
    operations: readonly (TableEditOperation | NamedEditOperation)[];
    paragraphs: TableOperationParagraph[];
    /**
     * The 1-based indexes of the operations the model answered again after a re-ask
     * (`EditOperationsApplyInput.confirmedOperationIndexes`): a typed number Word would
     * paint beside its own is written as given.
     *
     * NULL, or absent
     *   Nothing is confirmed: every check asks.
     */
    confirmedOperationIndexes?: readonly number[] | null;
    /**
     * The 1-based indexes of the operations `applyOperationList` leaves out, skipped in place.
     *
     * NULL, or absent
     *   Nothing is left out.
     */
    operationIndexesLeftOut?: ReadonlySet<number>;
}

export type TableOperationReaskReason = "unknownParagraph" | "notATableCell" | "cellCountMismatch" | "mergedCellText" | "typedNumbering" | "templateRowDeleted" | "raggedTable" | "emptyText";

/**
 * Every reason a table operation is handed back for, with what the model's resend of the
 * same operation does (`OperationReaskPolicy`).
 *
 * CASES
 *   - typedNumbering -> written as given: the cell is written "3." beside the "3." Word
 *                       paints, which the model may mean (a number the list does not count)
 *   - every other    -> left out: the row or the cells named are not there, or the texts do
 *                       not fit them, so nothing as given exists to write
 */
export const TABLE_OPERATION_REASK_POLICIES = {
    unknownParagraph: LEFT_OUT,
    notATableCell: LEFT_OUT,
    cellCountMismatch: LEFT_OUT,
    mergedCellText: LEFT_OUT,
    typedNumbering: WRITTEN_AS_GIVEN,
    templateRowDeleted: LEFT_OUT,
    raggedTable: LEFT_OUT,
    emptyText: LEFT_OUT,
} as const satisfies Record<TableOperationReaskReason, OperationReaskPolicy>;

/**
 * The first table operation that cannot be applied. `operationIndex` is
 * 1-based, matching how the model was told to read its list (`edit_ops[2]`),
 * and `message` tells the model what to change before it resubmits.
 */
export class TableOperationReask extends Error {
    /** What the model's resend of this operation does, from the reason's row of `TABLE_OPERATION_REASK_POLICIES`. */
    readonly policy: OperationReaskPolicy;

    constructor(
        readonly operationIndex: number,
        readonly reason: TableOperationReaskReason,
        message: string,
    ) {
        super(message);
        this.name = "TableOperationReask";
        this.policy = TABLE_OPERATION_REASK_POLICIES[reason];
    }
}

export class TableOperations {
    /**
     * Checks every table operation of the model's list in order and reports
     * the row changes asked for. Paragraph operations in the list are skipped
     * but keep their place, so a re-ask names the operation by its position
     * in the list the model sent, the same `edit_ops[N]` the paragraph applier
     * uses. Throws `TableOperationReask` at the first table operation that
     * cannot be applied; nothing is partially returned.
     */
    static apply(input: TableOperationsApplyInput): TableOperationsApplyResult {
        const result: TableOperationsApplyResult = { rowInsertions: [], rowDeletions: [], tableInsertions: [] };
        const confirmedOperationIndexes = new Set(input.confirmedOperationIndexes ?? []);
        for (const [zeroBasedIndex, candidate] of input.operations.entries()) {
            /**
             * WHEN the operation is a paragraph operation
             *   CONSEQUENCE
             *     Skip it; `EditOperations.apply` owns it. Its slot still counts toward the index.
             */
            if (!isTableEditOperation(candidate)) continue;
            const operation = candidate;
            /**
             * EXAMPLE
             *   list = [replace_span, insert_table_row_after], `zeroBasedIndex` = 1 -> `operationIndex` = 2, the number the model sees in `edit_ops[2]`
             */
            const operationIndex = zeroBasedIndex + 1;
            /**
             * WHEN the model confirmed this operation and its reason has nothing as given to write
             *   CONSEQUENCE
             *     it is left out (`applyOperationList`); its slot still counts toward the index.
             */
            if (input.operationIndexesLeftOut?.has(operationIndex)) continue;
            const paragraph = TableOperations.paragraphNamed(operation.paragraph_id, input.paragraphs, operationIndex);
            if (operation.op === "insert_table_after") {
                result.tableInsertions.push(TableOperations.tableInsertion(operation, paragraph, operationIndex));
                continue;
            }
            const cell = TableOperations.innermostCell(paragraph, operation.op, operationIndex);
            if (operation.op === "delete_table_row") {
                /**
                 * WHEN the row is already a tracked deletion
                 *   CONSEQUENCE
                 *     Ask again: striking a struck row would write a second deletion record Word rejects.
                 */
                if (cell.rowRevision === "deleted") {
                    throw new TableOperationReask(operationIndex, "templateRowDeleted", `edit_ops[${operationIndex}] (delete_table_row): the row of "${operation.paragraph_id}" is already a tracked deletion — name a live row.`);
                }
                result.rowDeletions.push({ rowOfBookmark: operation.paragraph_id });
                continue;
            }
            result.rowInsertions.push(TableOperations.rowInsertion(operation, cell, input.paragraphs, operationIndex, confirmedOperationIndexes.has(operationIndex)));
        }
        return result;
    }

    /**
     * The checks for one `insert_table_row_after`: a live template row, one
     * text per cell in every new row, no text under a joined merge, and at
     * least one non-blank text overall.
     */
    private static rowInsertion(
        operation: Extract<TableEditOperation, { op: "insert_table_row_after" }>,
        cell: DocumentTableCell,
        paragraphs: TableOperationParagraph[],
        operationIndex: number,
        modelConfirmedTheOperation: boolean,
    ): DesiredTableRowInsertion {
        /**
         * WHEN the template row is a tracked deletion
         *   CONSEQUENCE
         *     Ask again: a row that is going away is not a template.
         */
        if (cell.rowRevision === "deleted") {
            throw new TableOperationReask(operationIndex, "templateRowDeleted", `edit_ops[${operationIndex}] (insert_table_row_after): the row of "${operation.paragraph_id}" is a tracked deletion — name a live row to insert after.`);
        }
        const rowCells = TableOperations.cellsOfRow(cell, paragraphs);
        const rowDescription = rowCells.map(({ cell: rowCell }) => `cell ${rowCell.cellNumber}${rowCell.columnHeading === null ? "" : ` "${rowCell.columnHeading}"`}`).join(", ");
        operation.rows.forEach((cellTexts, rowIndex) => {
            /**
             * WHEN a new row has a different number of texts than the template row has cells
             *   CONSEQUENCE
             *     Ask again, listing the cells: the model counted grid columns, or skipped a merged cell.
             *
             * EXAMPLE
             *   cellCount = 4, cellTexts.length = 9 -> "needs 4 cell texts, 9 given"
             */
            if (cellTexts.length !== cell.cellCount) {
                throw new TableOperationReask(
                    operationIndex,
                    "cellCountMismatch",
                    `edit_ops[${operationIndex}] (insert_table_row_after): rows[${rowIndex}] needs ${cell.cellCount} cell texts, ${cellTexts.length} given — one per cell of the template row, in order: ${rowDescription}. Give "" for a cell left blank.`,
                );
            }
            /**
             * WHEN the new row joins a vertical merge and the model wrote text into the merged cell
             *   CONSEQUENCE
             *     Ask again: a continuation cell shows the cell above it, so the text would never be seen.
             */
            if (operation.merged_cells === "join") {
                for (const { cell: rowCell } of rowCells) {
                    const textForCell = cellTexts[rowCell.cellNumber - 1];
                    if (rowCell.verticalMerge !== "none" && textForCell.trim().length > 0) {
                        throw new TableOperationReask(
                            operationIndex,
                            "mergedCellText",
                            `edit_ops[${operationIndex}] (insert_table_row_after): rows[${rowIndex}] cell ${rowCell.cellNumber} continues a vertical merge under merged_cells "join", so it takes "" — or use merged_cells "start_new" to give it text.`,
                        );
                    }
                }
            }
            /**
             * WHEN the template cell is numbered by Word and the new text starts with a typed list marker
             *   CONSEQUENCE
             *     Ask again: the new row's cell inherits the numbering, so Word paints "3." and the typed "3." renders beside it as "3.  3.".
             *
             * EXAMPLE
             *   template "#" cell shows numbering="2.", text "3."  -> handed back, the model resubmits ""
             *   template "#" cell shows numbering="2.", text ""    -> allowed, Word paints "3."
             *
             * WHEN the model confirmed the operation after this ask, and the core sent it again
             *   CONSEQUENCE
             *     written as given (`TABLE_OPERATION_REASK_POLICIES`): nothing is asked.
             */
            if (modelConfirmedTheOperation) return;
            for (const { cell: rowCell, isAutoNumbered } of rowCells) {
                const textForCell = cellTexts[rowCell.cellNumber - 1].trim();
                if (isAutoNumbered && TYPED_LIST_MARKER_AT_START.test(textForCell)) {
                    throw new TableOperationReask(
                        operationIndex,
                        "typedNumbering",
                        `edit_ops[${operationIndex}] (insert_table_row_after): rows[${rowIndex}] cell ${rowCell.cellNumber} is numbered by Word (the section shows it with numbering=), so "${textForCell}" would render beside the number Word paints — give that cell "" and let Word number it.`,
                    );
                }
            }
        });
        /**
         * WHEN every text of every new row is blank
         *   CONSEQUENCE
         *     Ask again: an empty row is not a change the model meant to propose.
         */
        if (operation.rows.every((cellTexts) => cellTexts.every((text) => text.trim().length === 0))) {
            throw new TableOperationReask(operationIndex, "emptyText", `edit_ops[${operationIndex}] (insert_table_row_after): every cell text is blank — give the new row its text.`);
        }
        return { afterRowOfBookmark: operation.paragraph_id, rows: operation.rows.map((cellTexts) => [...cellTexts]), mergedCells: operation.merged_cells };
    }

    /**
     * The checks for one `insert_table_after`: a placement the anchor allows,
     * every row the same width, and at least one non-blank text.
     */
    private static tableInsertion(operation: Extract<TableEditOperation, { op: "insert_table_after" }>, paragraph: TableOperationParagraph, operationIndex: number): DesiredTableInsertion {
        /**
         * WHEN the model asks for "after_table" on a paragraph outside every table
         *   CONSEQUENCE
         *     Ask again: there is no table to go after; "after_paragraph" is what it meant.
         */
        if (operation.place === "after_table" && paragraph.tableCellPath.length === 0) {
            throw new TableOperationReask(
                operationIndex,
                "notATableCell",
                `edit_ops[${operationIndex}] (insert_table_after): place "after_table" needs a cell paragraph (shown with table=) — for "${operation.paragraph_id}" use place "after_paragraph".`,
            );
        }
        const columnCount = operation.rows[0].length;
        const raggedRowIndex = operation.rows.findIndex((cellTexts) => cellTexts.length !== columnCount);
        /**
         * WHEN a row has a different number of texts than the first row
         *   CONSEQUENCE
         *     Ask again: a new table is a plain grid, every row as wide as the header row.
         */
        if (raggedRowIndex !== -1) {
            throw new TableOperationReask(
                operationIndex,
                "raggedTable",
                `edit_ops[${operationIndex}] (insert_table_after): rows[${raggedRowIndex}] has ${operation.rows[raggedRowIndex].length} cell texts but the header row has ${columnCount} — give every row ${columnCount} texts, "" for a blank cell.`,
            );
        }
        if (operation.rows.every((cellTexts) => cellTexts.every((text) => text.trim().length === 0))) {
            throw new TableOperationReask(operationIndex, "emptyText", `edit_ops[${operationIndex}] (insert_table_after): every cell text is blank — give the table its text.`);
        }
        return { afterBookmark: operation.paragraph_id, rows: operation.rows.map((cellTexts) => [...cellTexts]), place: operation.place };
    }

    /**
     * The cell the named paragraph is written in: the last entry of its path.
     *
     * WHEN the paragraph is outside every table
     *   CONSEQUENCE
     *     Ask again: row operations need a row, and the prompt marks cell paragraphs with `table=`.
     */
    private static innermostCell(paragraph: TableOperationParagraph, operationName: string, operationIndex: number): DocumentTableCell {
        const innermost = paragraph.tableCellPath[paragraph.tableCellPath.length - 1];
        if (innermost === undefined) {
            throw new TableOperationReask(operationIndex, "notATableCell", `edit_ops[${operationIndex}] (${operationName}): the paragraph is not in a table — use a paragraph_id shown with table= in the section.`);
        }
        return innermost;
    }

    /**
     * One entry per cell of the row `cell` belongs to, in cell order, read
     * from the paragraphs whose innermost cell shares its table and row; a
     * cell counts as numbered when any of its paragraphs is.
     *
     * EXAMPLE
     *   row 6 of table 1 has cells 1, 2, 3 with paragraphs in each -> three entries, cellNumber 1, 2, 3
     */
    private static cellsOfRow(cell: DocumentTableCell, paragraphs: TableOperationParagraph[]): TemplateRowCell[] {
        const cellsByNumber = new Map<number, TemplateRowCell>();
        for (const paragraph of paragraphs) {
            const innermost = paragraph.tableCellPath[paragraph.tableCellPath.length - 1];
            if (innermost === undefined || innermost.tableNumber !== cell.tableNumber || innermost.rowNumber !== cell.rowNumber) continue;
            const known = cellsByNumber.get(innermost.cellNumber);
            cellsByNumber.set(innermost.cellNumber, { cell: innermost, isAutoNumbered: (known?.isAutoNumbered ?? false) || paragraph.isAutoNumbered });
        }
        return [...cellsByNumber.values()].sort((left, right) => left.cell.cellNumber - right.cell.cellNumber);
    }

    /**
     * WHEN no paragraph carries the bookmark the operation names
     *   CONSEQUENCE
     *     Ask again: the model invented or mistyped a paragraph_id and must pick one from the section it was shown.
     */
    private static paragraphNamed(paragraphId: string, paragraphs: TableOperationParagraph[], operationIndex: number): TableOperationParagraph {
        const paragraph = paragraphs.find((candidate) => candidate.bookmarkNames.includes(paragraphId));
        if (paragraph === undefined) {
            throw new TableOperationReask(operationIndex, "unknownParagraph", `edit_ops[${operationIndex}]: paragraph_id "${paragraphId}" is not a paragraph of this section — use one of the paragraph_id values shown in the section.`);
        }
        return paragraph;
    }
}
